import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import {
    AUTH_ERROR_CODES,
    AuthService,
} from "../services/auth.service";
import {
    faCog,
    faKey,
    faClose,
    faExclamationCircle,
    faInfoCircle, faLock,
} from "@fortawesome/pro-solid-svg-icons";
import { environment } from "../../environments/environment";
import { Meta } from "@angular/platform-browser";
import { AuthService as AuthServiceSSO } from "@auth0/auth0-angular";
import {faCircleExclamation, faSchoolCircleXmark} from "@fortawesome/pro-light-svg-icons";

@Component({
    selector: "fmui-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.less"],
})
export class LoginComponent implements OnInit {
    public step: "choose-organization" | "login" | "mfa" = "login";

    loginForm: UntypedFormGroup;
    mfaForm: UntypedFormGroup;

    mfaToken: string;
    usernameSubmitted: string;
    username: string;

    loginFailed: boolean = false;
    mfaFailed: boolean = false;
    showTerms: boolean = false;
    returnUrl: string = "/r";
    loginBusy: boolean;
    loginSSOBusy: boolean;
    isSSOFlow: boolean = localStorage.getItem("isSSOFlow") === "true";
    passwordBlurred: boolean;

    loggedOut: boolean = false;
    isLockedOut: boolean = false;

    errorCode: number = null;
    errorCodes = AUTH_ERROR_CODES;
    auth0Error: string = null;

    faCog = faCog;
    faKey = faKey;
    faClose = faClose;
    faExclamationCircle = faExclamationCircle;
    faInfoCircle = faInfoCircle;

    envBaseUrl = environment.baseUrl;

    constructor(
        public authService: AuthService,
        public authServiceSSO: AuthServiceSSO,
        private router: Router,
        private route: ActivatedRoute,
        private fb: UntypedFormBuilder,
        private meta: Meta,
    ) {}

    ngOnInit() {
        this.loginForm = this.fb.group({
            username: [
                { value: "", disabled: false },
                [Validators.required, Validators.minLength(1)],
            ],
            password: [
                { value: "", disabled: false },
                [Validators.required, Validators.minLength(1)],
            ],
        });

        this.mfaForm = this.fb.group({
            mfaCode: [{ value: "", disabled: false }, [Validators.required]],
        });
        // this.errorCode = parseInt(this.route.snapshot.params['errorCode'], 10) || null;

        // undoChanges login status
        // this.authService.logout();

        // TODO: fix queryParams
        // this.returnUrl = this.route.snapshot.params['url'] || '/r';

        this.route.params.subscribe((params) => {
            this.errorCode = params.errorCode
                ? parseInt(params["errorCode"], 10)
                : this.errorCode;
            this.returnUrl = params.url ? params["url"] : this.returnUrl;

            if (params.logout) {
                this.authService.logout();
                this.loggedOut = true;
            }
        });

        // Set meta tags to prevent indexing
        // The robots meta tag instructs search engines not to show the page in search results
        this.meta.addTag({ name: "robots", content: "noindex, nofollow" });

        //SSO Flow
        this.authServiceSSO.error$.subscribe((err) => {
            this.auth0Error = err.message;
        })

        localStorage.setItem("isSSOFlow", "false");
        this.loginBusy = false;
        this.loginSSOBusy = false;
    }

    attemptLoginWithAuth0() {
        try {
            localStorage.setItem("isSSOFlow", "true");
            this.authService.loginWithSSO();
            this.loginSSOBusy = true;
            setTimeout(() => {
                // handle loginSSOBusy in setTimeout due to auth0 redirect
                this.loginSSOBusy = false;
                this.loginBusy = false;
            }, 2000)
        } catch (error) {
            alert("Error while trying to login with Auth0");
        }
    }

    attemptLogin() {
        // TODO: Might need to clear all messages (error, infos, successes) like in SCMUIV2

        localStorage.removeItem("isSSOFlow");
        this.loginFailed = false;
        this.loggedOut = false;
        this.isLockedOut = false;
        this.auth0Error = null;
        this.loginBusy = true;
        this.loginForm.disable();
        this.usernameSubmitted = this.loginForm.get("username").value;

        this.authService
            .login(
                this.loginForm.value["username"],
                this.loginForm.value["password"]
            )
            .subscribe(
                (result) => {
                    if (result.status === "SUCCESS") {
                        this.loginForm.reset();
                        this.router
                            .navigateByUrl(this.returnUrl)
                            .catch((error) => {
                                console.warn(
                                    "Something is wrong with requested login url ",
                                    error
                                );
                                this.router.navigate(["/r"]);
                            });
                        return;
                    } else if (result.status === "MFA_REQUIRED") {
                        this.mfaToken = result.mfaToken;
                        this.step = "mfa";
                        this.loginBusy = false;
                        return;
                    }

                    console.warn("Unexpected login result: ", result);
                },
                (error) => {
                    console.error(error);
                    this.checkErrorCodes(error, false);
                    this.loginBusy = false;
                    this.loginForm.enable();
                }
            );
    }

    checkErrorCodes(error, isMfa) {
        switch (error.error.error_description.split(":")[0]) {
            case "CONTROLANT_AUTHSERVER_ERROR_02":
            case "CONTROLANT_AUTHSERVER_ERROR_03":
                this.isLockedOut = true;
                break;
            default:
                isMfa ? (this.mfaFailed = true) : (this.loginFailed = true);
                break;
        }
    }

    attemptMfaLogin() {
        // TODO: Might need to clear all messages (error, infos, successes) like in SCMUIV2

        this.mfaFailed = false;
        this.isLockedOut = false;
        this.loginBusy = true;
        this.loginForm.disable();

        this.authService
            .loginWithMfa(this.mfaToken, this.mfaForm.value["mfaCode"])
            .subscribe(
                (result) => {
                    if (result.status === "SUCCESS") {
                        this.loginForm.reset();
                        this.router
                            .navigateByUrl(this.returnUrl)
                            .catch((error) => {
                                console.warn(
                                    "Something is wrong with requested login url ",
                                    error
                                );
                                this.router.navigate(["/r"]);
                            });
                        return;
                    }
                    console.warn("Unexpected login result: ", result);
                },
                (error) => {
                    console.error(error);
                    this.loginBusy = false;
                    this.checkErrorCodes(error, true);
                    this.loginForm.enable();
                }
            );
    }

    protected readonly faCircleExclamation = faCircleExclamation;
}
